import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { auth, googleProvider, db } from "config/firebase";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import {
  doc,
  updateDoc,
  setDoc,
  onSnapshot,
  deleteDoc,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  getDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { APP_CONFIG } from "config/appConfig";

const AdminContext = createContext();

// Admin email whitelist
const ADMIN_EMAILS = [process.env.MY_EMAIL || "santhosh.sivkumar@gmail.com"];

export const AdminProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminMode, setAdminMode] = useState(() => {
    const saved = localStorage.getItem("admin_mode");
    return saved !== null
      ? saved === "true"
      : (APP_CONFIG.enableEditMode ?? false);
  });
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [themesPanelOpen, setThemesPanelOpen] = useState(false);
  const [expandUnsavedChanges, setExpandUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draftChanges, setDraftChanges] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Check authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsAdmin(
        currentUser ? ADMIN_EMAILS.includes(currentUser.email) : false,
      );
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load draft changes from Firestore with real-time listener
  // ONLY when admin panel is open
  useEffect(() => {
    if (!isAdmin || !adminPanelOpen) return;

    const draftSections = [
      "skills",
      "certificates",
      "projects",
      "contact",
      "qualification",
      "about",
      "home",
      "defaultThemes",
    ];
    const changes = {};

    // Set up listeners for all drafts
    const unsubscribers = draftSections.map((section) => {
      const draftRef = doc(db, "drafts", section);
      return onSnapshot(
        draftRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            changes[section] = true;
          } else {
            delete changes[section];
          }
          setDraftChanges({ ...changes });
          setHasUnsavedChanges(Object.keys(changes).length > 0);
        },
        (error) => {
          console.error(`Error loading draft for ${section}:`, error);
          // Fallback: at least update the count if draft exists
        },
      );
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [isAdmin, adminPanelOpen]);

  // Sign in with Google with timeout and improved error handling
  const signInWithGoogle = async () => {
    const SIGN_IN_TIMEOUT = 30000; // 30 seconds timeout

    try {
      // Create a promise that rejects after timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("TIMEOUT"));
        }, SIGN_IN_TIMEOUT);
      });

      // Race between sign-in and timeout
      const result = await Promise.race([
        signInWithPopup(auth, googleProvider),
        timeoutPromise,
      ]);

      const userEmail = result.user.email;

      if (!ADMIN_EMAILS.includes(userEmail)) {
        await signOut(auth);
        const error = new Error("Unauthorized: You are not an admin.");
        error.code = "UNAUTHORIZED";
        error._intruderUser = result.user; // carry profile into catch block
        throw error;
      }

      return { user: result.user, success: true };
    } catch (error) {
      console.error("Sign in error:", error);

      // Categorize the error for better user feedback
      let errorData = {
        success: false,
        code: error.code || "UNKNOWN",
        message: error.message,
      };

      // Handle specific error codes
      if (error.message === "TIMEOUT") {
        errorData = {
          success: false,
          code: "TIMEOUT",
          message:
            "Sign in took too long. Please check your connection and try again.",
          displayMessage: "Connection timeout. Try again.",
        };
      } else if (error.code === "auth/popup-closed-by-user") {
        errorData = {
          success: false,
          code: "POPUP_CLOSED",
          message: "Sign in cancelled by user.",
          displayMessage: "Sign-in cancelled. Please try again.",
        };
      } else if (error.code === "auth/popup-blocked") {
        errorData = {
          success: false,
          code: "POPUP_BLOCKED",
          message: "Popup was blocked by the browser.",
          displayMessage: "Enable popups and try again",
        };
      } else if (error.code === "UNAUTHORIZED") {
        errorData = {
          success: false,
          code: "UNAUTHORIZED",
          message: "Your email is not authorized to access admin panel.",
          displayMessage: "Only Santhosh S can access this",
        };
        // Fire-and-forget security alert — do not await, never blocks sign-out flow
        const intrusionDateTime = new Date().toLocaleString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        const intrusionPayload = {
          source: "admin_intrusion",
          googleName: error._intruderUser?.displayName || null,
          googleEmail: error._intruderUser?.email || null,
          googlePhotoUrl: error._intruderUser?.photoURL || null,
          googleUid: error._intruderUser?.uid || null,
          userAgent: navigator.userAgent,
          dateTime: intrusionDateTime,
        };
        axios
          .post("/.netlify/functions/send-visitor-details", intrusionPayload)
          .catch(() => {}); // silent — never surfaces to user
        addDoc(collection(db, "Portfolio Events"), {
          ...intrusionPayload,
          timestamp: serverTimestamp(),
        }).catch(() => {});
      } else if (
        error.code === "auth/network-request-failed" ||
        error.message?.includes("network")
      ) {
        errorData = {
          success: false,
          code: "NETWORK_ERROR",
          message: "Network error. Please check your internet connection.",
          displayMessage: "Check your connection",
        };
      } else {
        errorData = {
          success: false,
          code: error.code || "UNKNOWN",
          message: error.message,
          displayMessage: "Sign-in failed",
        };
      }

      throw errorData;
    }
  };

  // Sign out
  const signOutUser = async () => {
    try {
      await signOut(auth);
      setAdminMode(false);
      localStorage.removeItem("admin_mode");
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  };

  // Toggle admin mode
  const toggleAdminMode = () => {
    if (isAdmin) {
      const newMode = !adminMode;
      setAdminMode(newMode);
      localStorage.setItem("admin_mode", newMode.toString());

      setAdminPanelOpen(false);
      setThemesPanelOpen(false);
    }
  };

  // Toggle admin panel
  const toggleAdminPanel = () => {
    const newState = !adminPanelOpen;
    setAdminPanelOpen(newState);
    // Close themes panel if opening admin
    if (newState) {
      setThemesPanelOpen(false);
    }
  };

  // Close admin panel
  const closeAdminPanel = () => {
    setAdminPanelOpen(false);
  };

  // Open admin panel
  const openAdminPanel = () => {
    setAdminPanelOpen(true);
    // Close themes panel if opening admin
    setThemesPanelOpen(false);
  };

  // Open admin panel and expand unsaved changes accordion
  const openAdminPanelWithChanges = () => {
    setAdminPanelOpen(true);
    setExpandUnsavedChanges(true);
    setThemesPanelOpen(false);
  };

  // Toggle themes panel
  const toggleThemesPanel = () => {
    const newState = !themesPanelOpen;
    setThemesPanelOpen(newState);
    // Close admin panel if opening themes
    if (newState) {
      setAdminPanelOpen(false);
    }
  };

  // Close themes panel
  const closeThemesPanel = () => {
    setThemesPanelOpen(false);
  };

  // Save draft changes
  // Save fetched data to portfolio_local_db (for future fast loading)
  const saveFetchedData = (sectionId, data) => {
    try {
      const localDb = JSON.parse(
        localStorage.getItem("portfolio_local_db") || "{}",
      );
      localDb[sectionId] = {
        data: data,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem("portfolio_local_db", JSON.stringify(localDb));
    } catch (error) {
      console.error("Error saving to local db:", error);
    }
  };
  const [skillsDraft, setSkillsDraft] = useState(null);

  // Load skills draft from Firestore - ONLY when admin is authenticated
  useEffect(() => {
    if (!isAdmin) {
      setSkillsDraft(null);
      return;
    }

    const skillsRef = doc(db, "drafts", "skills");
    const unsubscribe = onSnapshot(
      skillsRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setSkillsDraft(docSnapshot.data());
        } else {
          setSkillsDraft(null);
        }
      },
      (error) => console.error("Error loading skills draft:", error),
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Helper function to update localStorage with published data
  const updateLocalStorageCache = (sectionId, data) => {
    try {
      const localDb = JSON.parse(
        localStorage.getItem("portfolio_local_db") || "{}",
      );
      if (!localDb[sectionId]) {
        localDb[sectionId] = {};
      }
      localDb[sectionId].data = data;
      localDb[sectionId].timestamp = new Date().toISOString();
      localStorage.setItem("portfolio_local_db", JSON.stringify(localDb));
    } catch (error) {
      console.error("Error updating localStorage cache:", error);
    }
  };

  // Save skills draft to Firestore
  const saveSkillsDraft = async (skillsData) => {
    try {
      const skillsRef = doc(db, "drafts", "skills");
      await setDoc(skillsRef, {
        ...skillsData,
        lastModified: serverTimestamp(),
      });
      setSkillsDraft(skillsData);
      openAdminPanelWithChanges();
    } catch (error) {
      console.error("Error saving skills draft:", error);
      throw error;
    }
  };

  // Publish skills draft to Firestore
  const publishSkillsDraft = async () => {
    if (!skillsDraft) return false;

    try {
      const skillsDocRef = doc(db, "skills", "main");
      await updateDoc(skillsDocRef, skillsDraft);

      // Save version before clearing draft
      saveVersion("skills", skillsDraft);

      // Update localStorage cache with published data
      updateLocalStorageCache("skills", skillsDraft);

      // Clear draft immediately - onSnapshot will auto-update the data
      setSkillsDraft(null);
      const skillsRef = doc(db, "drafts", "skills");
      await deleteDoc(skillsRef);

      return true;
    } catch (error) {
      console.error("Error publishing skills:", error);
      return false;
    }
  };

  // Discard skills draft from Firestore
  const discardSkillsDraft = async () => {
    try {
      const skillsRef = doc(db, "drafts", "skills");
      await deleteDoc(skillsRef);
      setSkillsDraft(null);
    } catch (error) {
      console.error("Error discarding skills draft:", error);
      throw error;
    }
  };

  // Certificates draft management
  const [certificatesDraft, setCertificatesDraft] = useState(null);

  // Load certificates draft from Firestore - ONLY when admin is authenticated
  useEffect(() => {
    if (!isAdmin) {
      setCertificatesDraft(null);
      return;
    }

    const certificatesRef = doc(db, "drafts", "certificates");
    const unsubscribe = onSnapshot(
      certificatesRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const draftData = docSnapshot.data();
          setCertificatesDraft(draftData?.items || draftData);
        } else {
          setCertificatesDraft(null);
        }
      },
      (error) => console.error("Error loading certificates draft:", error),
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Save certificates draft to Firestore
  const saveCertificatesDraft = async (certificatesData) => {
    try {
      const certificatesRef = doc(db, "drafts", "certificates");
      await setDoc(certificatesRef, {
        items: certificatesData,
        lastModified: serverTimestamp(),
      });
      setCertificatesDraft(certificatesData);
      openAdminPanelWithChanges();
    } catch (error) {
      console.error("Error saving certificates draft:", error);
      throw error;
    }
  };

  // Publish certificates draft to Firestore
  const publishCertificatesDraft = async () => {
    if (!certificatesDraft) return false;

    try {
      const certificatesDocRef = doc(db, "certificates", "main");
      await updateDoc(certificatesDocRef, { items: certificatesDraft });

      // Save version before clearing draft
      saveVersion("certificates", certificatesDraft);

      // Update localStorage cache with published data
      updateLocalStorageCache("certificates", certificatesDraft);

      // Clear draft immediately - onSnapshot will auto-update the data
      setCertificatesDraft(null);
      const certificatesRef = doc(db, "drafts", "certificates");
      await deleteDoc(certificatesRef);

      return true;
    } catch (error) {
      console.error("Error publishing certificates:", error);
      return false;
    }
  };

  // Discard certificates draft from Firestore
  const discardCertificatesDraft = async () => {
    try {
      const certificatesRef = doc(db, "drafts", "certificates");
      await deleteDoc(certificatesRef);
      setCertificatesDraft(null);
    } catch (error) {
      console.error("Error discarding certificates draft:", error);
      throw error;
    }
  };

  // Projects draft management
  const [projectsDraft, setProjectsDraft] = useState(null);

  // Load projects draft from Firestore - ONLY when admin is authenticated
  useEffect(() => {
    if (!isAdmin) {
      setProjectsDraft(null);
      return;
    }

    const projectsRef = doc(db, "drafts", "projects");
    const unsubscribe = onSnapshot(
      projectsRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const draftData = docSnapshot.data();
          setProjectsDraft(draftData?.items || draftData);
        } else {
          setProjectsDraft(null);
        }
      },
      (error) => console.error("Error loading projects draft:", error),
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Save projects draft to Firestore
  const saveProjectsDraft = async (projectsData) => {
    try {
      const projectsRef = doc(db, "drafts", "projects");
      await setDoc(projectsRef, {
        items: projectsData,
        lastModified: serverTimestamp(),
      });
      setProjectsDraft(projectsData);
      openAdminPanelWithChanges();
    } catch (error) {
      console.error("Error saving projects draft:", error);
      throw error;
    }
  };

  // Publish projects draft to Firestore
  const publishProjectsDraft = async () => {
    if (!projectsDraft) return false;

    try {
      const projectsDocRef = doc(db, "projects", "main");
      await updateDoc(projectsDocRef, { items: projectsDraft });

      // Save version before clearing draft
      saveVersion("projects", projectsDraft);

      // Update localStorage cache with published data (wrap in { items } to match Firestore doc shape)
      updateLocalStorageCache("projects", { items: projectsDraft });

      // Clear draft immediately - onSnapshot will auto-update the data
      setProjectsDraft(null);
      const projectsRef = doc(db, "drafts", "projects");
      await deleteDoc(projectsRef);

      return true;
    } catch (error) {
      console.error("Error publishing projects:", error);
      return false;
    }
  };

  // Discard projects draft from Firestore
  const discardProjectsDraft = async () => {
    try {
      const projectsRef = doc(db, "drafts", "projects");
      await deleteDoc(projectsRef);
      setProjectsDraft(null);
    } catch (error) {
      console.error("Error discarding projects draft:", error);
      throw error;
    }
  };

  // Contact draft management
  const [contactDraft, setContactDraft] = useState(null);

  // Load contact draft from Firestore - ONLY when admin is authenticated
  useEffect(() => {
    if (!isAdmin) {
      setContactDraft(null);
      return;
    }

    const contactRef = doc(db, "drafts", "contact");
    const unsubscribe = onSnapshot(
      contactRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setContactDraft(docSnapshot.data());
        } else {
          setContactDraft(null);
        }
      },
      (error) => console.error("Error loading contact draft:", error),
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Save contact draft to Firestore
  const saveContactDraft = async (contactData) => {
    try {
      const contactRef = doc(db, "drafts", "contact");
      await setDoc(contactRef, {
        ...contactData,
        lastModified: serverTimestamp(),
      });
      setContactDraft(contactData);
      openAdminPanelWithChanges();
    } catch (error) {
      console.error("Error saving contact draft:", error);
      throw error;
    }
  };

  // Publish contact draft to Firestore
  const publishContactDraft = async () => {
    if (!contactDraft) return false;

    try {
      const contactDocRef = doc(db, "contact", "main");
      await updateDoc(contactDocRef, contactDraft);

      // Save version before clearing draft
      saveVersion("contact", contactDraft);

      // Update localStorage cache with published data
      updateLocalStorageCache("contact", contactDraft);

      // Clear draft immediately - onSnapshot will auto-update the data
      setContactDraft(null);
      const contactRef = doc(db, "drafts", "contact");
      await deleteDoc(contactRef);

      return true;
    } catch (error) {
      console.error("Error publishing contact:", error);
      return false;
    }
  };

  // Discard contact draft from Firestore
  const discardContactDraft = async () => {
    try {
      const contactRef = doc(db, "drafts", "contact");
      await deleteDoc(contactRef);
      setContactDraft(null);
    } catch (error) {
      console.error("Error discarding contact draft:", error);
      throw error;
    }
  };

  // Qualification draft management
  const [qualificationDraft, setQualificationDraft] = useState(null);

  // Load qualification draft from Firestore - ONLY when admin is authenticated
  useEffect(() => {
    if (!isAdmin) {
      setQualificationDraft(null);
      return;
    }

    const qualificationRef = doc(db, "drafts", "qualification");
    const unsubscribe = onSnapshot(
      qualificationRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setQualificationDraft(docSnapshot.data());
        } else {
          setQualificationDraft(null);
        }
      },
      (error) => console.error("Error loading qualification draft:", error),
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Save qualification draft to Firestore
  const saveQualificationDraft = async (qualificationData) => {
    try {
      const qualificationRef = doc(db, "drafts", "qualification");
      await setDoc(qualificationRef, {
        ...qualificationData,
        lastModified: serverTimestamp(),
      });
      setQualificationDraft(qualificationData);
      openAdminPanelWithChanges();
    } catch (error) {
      console.error("Error saving qualification draft:", error);
      throw error;
    }
  };

  // Publish qualification draft to Firestore
  const publishQualificationDraft = async () => {
    if (!qualificationDraft) return false;

    try {
      const qualificationDocRef = doc(db, "qualification", "main");
      await updateDoc(qualificationDocRef, qualificationDraft);

      // Save version before clearing draft
      saveVersion("qualification", qualificationDraft);

      // Update localStorage cache with published data
      updateLocalStorageCache("qualification", qualificationDraft);

      // Clear draft immediately - onSnapshot will auto-update the data
      setQualificationDraft(null);
      const qualificationRef = doc(db, "drafts", "qualification");
      await deleteDoc(qualificationRef);

      return true;
    } catch (error) {
      console.error("Error publishing qualification:", error);
      return false;
    }
  };

  // Discard qualification draft from Firestore
  const discardQualificationDraft = async () => {
    try {
      const qualificationRef = doc(db, "drafts", "qualification");
      await deleteDoc(qualificationRef);
      setQualificationDraft(null);
    } catch (error) {
      console.error("Error discarding qualification draft:", error);
      throw error;
    }
  };

  // About draft management
  const [aboutDraft, setAboutDraft] = useState(null);

  // Load about draft from Firestore - ONLY when admin is authenticated
  useEffect(() => {
    if (!isAdmin) {
      setAboutDraft(null);
      return;
    }

    const aboutRef = doc(db, "drafts", "about");
    const unsubscribe = onSnapshot(
      aboutRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setAboutDraft(docSnapshot.data());
        } else {
          setAboutDraft(null);
        }
      },
      (error) => console.error("Error loading about draft:", error),
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Save about draft to Firestore
  const saveAboutDraft = async (aboutData) => {
    try {
      const aboutRef = doc(db, "drafts", "about");
      await setDoc(aboutRef, {
        ...aboutData,
        lastModified: serverTimestamp(),
      });
      setAboutDraft(aboutData);
      openAdminPanelWithChanges();
    } catch (error) {
      console.error("Error saving about draft:", error);
      throw error;
    }
  };

  // Publish about draft to Firestore
  const publishAboutDraft = async () => {
    if (!aboutDraft) return false;

    try {
      const aboutDocRef = doc(db, "about", "main");
      await updateDoc(aboutDocRef, aboutDraft);

      // Save version before clearing draft
      saveVersion("about", aboutDraft);

      // Update localStorage cache with published data
      updateLocalStorageCache("about", aboutDraft);

      // Clear draft immediately - onSnapshot will auto-update the data
      setAboutDraft(null);
      const aboutRef = doc(db, "drafts", "about");
      await deleteDoc(aboutRef);

      return true;
    } catch (error) {
      console.error("Error publishing about:", error);
      return false;
    }
  };

  // Discard about draft from Firestore
  const discardAboutDraft = async () => {
    try {
      const aboutRef = doc(db, "drafts", "about");
      await deleteDoc(aboutRef);
      setAboutDraft(null);
    } catch (error) {
      console.error("Error discarding about draft:", error);
      throw error;
    }
  };

  // Home draft management
  const [homeDraft, setHomeDraft] = useState(null);

  // Load home draft from Firestore - ONLY when admin is authenticated
  useEffect(() => {
    if (!isAdmin) {
      setHomeDraft(null);
      return;
    }

    const homeRef = doc(db, "drafts", "home");
    const unsubscribe = onSnapshot(
      homeRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setHomeDraft(docSnapshot.data());
        } else {
          setHomeDraft(null);
        }
      },
      (error) => console.error("Error loading home draft:", error),
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Save home draft to Firestore
  const saveHomeDraft = async (homeData) => {
    try {
      const homeRef = doc(db, "drafts", "home");
      await setDoc(homeRef, {
        ...homeData,
        lastModified: serverTimestamp(),
      });
      setHomeDraft(homeData);
      openAdminPanelWithChanges();
    } catch (error) {
      console.error("Error saving home draft:", error);
      throw error;
    }
  };

  // Publish home draft to Firestore
  const publishHomeDraft = async () => {
    if (!homeDraft) return false;

    try {
      const homeDocRef = doc(db, "home", "main");
      await updateDoc(homeDocRef, homeDraft);

      // Save version before clearing draft
      saveVersion("home", homeDraft);

      // Update localStorage cache with published data
      updateLocalStorageCache("home", homeDraft);

      // Clear draft immediately - onSnapshot will auto-update the data
      setHomeDraft(null);
      const homeRef = doc(db, "drafts", "home");
      await deleteDoc(homeRef);

      return true;
    } catch (error) {
      console.error("Error publishing home:", error);
      return false;
    }
  };

  // Discard home draft from Firestore
  const discardHomeDraft = async () => {
    try {
      const homeRef = doc(db, "drafts", "home");
      await deleteDoc(homeRef);
      setHomeDraft(null);
    } catch (error) {
      console.error("Error discarding home draft:", error);
      throw error;
    }
  };

  // Default Themes draft management
  const [defaultThemesDraft, setDefaultThemesDraft] = useState(null);

  // Load defaultThemes draft from Firestore - ONLY when admin is authenticated
  useEffect(() => {
    if (!isAdmin) {
      setDefaultThemesDraft(null);
      return;
    }

    const defaultThemesRef = doc(db, "drafts", "defaultThemes");
    const unsubscribe = onSnapshot(
      defaultThemesRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setDefaultThemesDraft(docSnapshot.data());
        } else {
          setDefaultThemesDraft(null);
        }
      },
      (error) => console.error("Error loading defaultThemes draft:", error),
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Save defaultThemes draft to Firestore
  const saveDefaultThemesDraft = async (themesData) => {
    try {
      const defaultThemesRef = doc(db, "drafts", "defaultThemes");
      await setDoc(defaultThemesRef, {
        themes: themesData,
        lastModified: serverTimestamp(),
      });
      setDefaultThemesDraft({ themes: themesData });
      openAdminPanelWithChanges();
    } catch (error) {
      console.error("Error saving defaultThemes draft:", error);
      throw error;
    }
  };

  // Publish defaultThemes draft to Firestore
  const publishDefaultThemesDraft = async () => {
    if (!defaultThemesDraft) return false;

    try {
      const defaultThemesDocRef = doc(db, "defaultThemes", "main");
      await updateDoc(defaultThemesDocRef, {
        themes: defaultThemesDraft.themes || [],
      });

      // Save version before clearing draft
      saveVersion("defaultThemes", defaultThemesDraft);

      // Update localStorage cache with published data
      updateLocalStorageCache("defaultThemes", defaultThemesDraft);

      // Clear draft immediately
      setDefaultThemesDraft(null);
      const defaultThemesRef = doc(db, "drafts", "defaultThemes");
      await deleteDoc(defaultThemesRef);

      return true;
    } catch (error) {
      console.error("Error publishing defaultThemes:", error);
      return false;
    }
  };

  // Discard defaultThemes draft from Firestore
  const discardDefaultThemesDraft = async () => {
    try {
      const defaultThemesRef = doc(db, "drafts", "defaultThemes");
      await deleteDoc(defaultThemesRef);
      setDefaultThemesDraft(null);
    } catch (error) {
      console.error("Error discarding defaultThemes draft:", error);
      throw error;
    }
  };

  // Undo defaultThemes publish (restore from version history)
  const undoDefaultThemesPublish = async (versionId) => {
    try {
      const versionRef = doc(db, "versions", versionId);
      const versionSnap = await getDoc(versionRef);

      if (versionSnap.exists()) {
        const versionData = versionSnap.data();
        const defaultThemesDocRef = doc(db, "defaultThemes", "main");
        await updateDoc(defaultThemesDocRef, {
          themes: versionData.themes || [],
        });

        // Save new version for the undo
        saveVersion("defaultThemes", versionData);

        // Update localStorage
        updateLocalStorageCache("defaultThemes", versionData);

        return true;
      }
    } catch (error) {
      console.error("Error undoing defaultThemes publish:", error);
      return false;
    }
  };

  // VERSION MANAGEMENT FUNCTIONS
  // Save a version to portfolio_version_history (5 overall versions, not per section)
  const saveVersion = async (sectionId, data) => {
    try {
      const versionsRef = collection(db, "versions");

      // Query all versions to ensure we maintain the 5-version limit globally
      const q = query(versionsRef, orderBy("timestamp", "desc"), limit(5));
      const snapshot = await getDocs(q);

      // If we already have 5 versions, delete the oldest one
      if (snapshot.docs.length >= 5) {
        const oldestDoc = snapshot.docs[snapshot.docs.length - 1];
        await deleteDoc(oldestDoc.ref);
      }

      // Get all versions for this section to calculate correct version_num
      const sectionVersions = snapshot.docs
        .filter((doc) => doc.data().sectionId === sectionId)
        .map((doc) => doc.data());

      // Version number is based on count of existing versions for this section + 1
      const versionNum = sectionVersions.length + 1;

      // Create new version document
      const newVersionRef = doc(versionsRef);
      const newVersion = {
        id: newVersionRef.id,
        sectionId: sectionId,
        data: data,
        version_num: versionNum,
        timestamp: new Date(),
      };

      await setDoc(newVersionRef, newVersion);
      return newVersionRef.id;
    } catch (error) {
      console.error("Error saving version:", error);
      return null;
    }
  };

  // Get version history for a section (filtered from overall history)
  const getVersionHistoryBySectionId = async (sectionId) => {
    try {
      const versionsRef = collection(db, "versions");
      const q = query(versionsRef, orderBy("timestamp", "desc"), limit(5));
      const snapshot = await getDocs(q);

      return snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }))
        .filter((v) => v.sectionId === sectionId);
    } catch (error) {
      console.error("Error getting version history:", error);
      return [];
    }
  };

  // Delete a specific version
  const deleteVersion = async (versionId) => {
    try {
      const versionRef = doc(db, "versions", versionId);
      await deleteDoc(versionRef);
    } catch (error) {
      console.error("Error deleting version:", error);
    }
  };

  // Get all versions overall (max 5)
  const getAllVersions = async () => {
    try {
      const versionsRef = collection(db, "versions");
      const q = query(versionsRef, orderBy("timestamp", "desc"), limit(5));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
    } catch (error) {
      console.error("Error getting all versions:", error);
      return [];
    }
  };

  // Publish all drafts
  const publishAllDrafts = async () => {
    try {
      await Promise.all([
        publishSkillsDraft(),
        publishCertificatesDraft(),
        publishProjectsDraft(),
        publishAboutDraft(),
        publishContactDraft(),
        publishQualificationDraft(),
        publishHomeDraft(),
      ]);
    } catch (error) {
      console.error("Error publishing all drafts:", error);
      throw error;
    }
  };

  // Discard all drafts
  const discardDrafts = async () => {
    try {
      await Promise.all([
        discardSkillsDraft(),
        discardCertificatesDraft(),
        discardProjectsDraft(),
        discardAboutDraft(),
        discardContactDraft(),
        discardQualificationDraft(),
        discardHomeDraft(),
      ]);
      setExpandUnsavedChanges(false); // Close the unsaved changes accordion after discarding
    } catch (error) {
      console.error("Error discarding all drafts:", error);
      throw error;
    }
  };

  // Helper function to get the most recent published version (first in history)
  const getLastPublishedVersion = (sectionId) => {
    const history = getVersionHistoryBySectionId(sectionId);
    if (history.length > 0) {
      return history[0]; // First is newest
    }
    return null;
  };

  // Helper function to deep compare objects
  const isDataEqual = (obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };

  // Get change count for each section
  const getChangesBySection = () => {
    return {
      about: aboutDraft ? 1 : 0,
      contact: contactDraft ? 1 : 0,
      qualification: qualificationDraft ? 1 : 0,
      home: homeDraft ? 1 : 0,
      skills: skillsDraft ? 1 : 0,
      certificates: certificatesDraft ? 1 : 0,
      projects: projectsDraft ? 1 : 0,
      defaultThemes: defaultThemesDraft ? 1 : 0,
    };
  };

  // Check if version data is different from last published version
  const isVersionDifferentFromLastPublished = (sectionId, versionData) => {
    const lastPublished = getLastPublishedVersion(sectionId);
    if (!lastPublished) return true; // No previous version, so it's "different"
    return !isDataEqual(versionData, lastPublished.data);
  };

  // UNDO FUNCTIONS - Restore previous version as draft
  const undoSkillsPublish = async (versionId) => {
    try {
      const history = await getVersionHistoryBySectionId("skills");
      const version = history.find((v) => v.id === versionId);
      if (version) {
        if (isVersionDifferentFromLastPublished("skills", version.data)) {
          const skillsRef = doc(db, "drafts", "skills");
          await setDoc(skillsRef, {
            ...version.data,
            lastModified: serverTimestamp(),
          });
          setSkillsDraft(version.data);
        } else {
          alert(
            "This version is the same as the current published state. No changes to restore.",
          );
        }
      }
    } catch (error) {
      console.error("Error undoing skills publish:", error);
    }
  };

  const undoCertificatesPublish = async (versionId) => {
    try {
      const history = await getVersionHistoryBySectionId("certificates");
      const version = history.find((v) => v.id === versionId);
      if (version) {
        if (isVersionDifferentFromLastPublished("certificates", version.data)) {
          const certificatesRef = doc(db, "drafts", "certificates");
          await setDoc(certificatesRef, {
            ...version.data,
            lastModified: serverTimestamp(),
          });
          setCertificatesDraft(version.data);
        } else {
          alert(
            "This version is the same as the current published state. No changes to restore.",
          );
        }
      }
    } catch (error) {
      console.error("Error undoing certificates publish:", error);
    }
  };

  const undoProjectsPublish = async (versionId) => {
    try {
      const history = await getVersionHistoryBySectionId("projects");
      const version = history.find((v) => v.id === versionId);
      if (version) {
        if (isVersionDifferentFromLastPublished("projects", version.data)) {
          const projectsRef = doc(db, "drafts", "projects");
          await setDoc(projectsRef, {
            ...version.data,
            lastModified: serverTimestamp(),
          });
          setProjectsDraft(version.data);
        } else {
          alert(
            "This version is the same as the current published state. No changes to restore.",
          );
        }
      }
    } catch (error) {
      console.error("Error undoing projects publish:", error);
    }
  };

  const undoAboutPublish = async (versionId) => {
    try {
      const history = await getVersionHistoryBySectionId("about");
      const version = history.find((v) => v.id === versionId);
      if (version) {
        if (isVersionDifferentFromLastPublished("about", version.data)) {
          const aboutRef = doc(db, "drafts", "about");
          await setDoc(aboutRef, {
            ...version.data,
            lastModified: serverTimestamp(),
          });
          setAboutDraft(version.data);
        } else {
          alert(
            "This version is the same as the current published state. No changes to restore.",
          );
        }
      }
    } catch (error) {
      console.error("Error undoing about publish:", error);
    }
  };

  const undoContactPublish = async (versionId) => {
    try {
      const history = await getVersionHistoryBySectionId("contact");
      const version = history.find((v) => v.id === versionId);
      if (version) {
        if (isVersionDifferentFromLastPublished("contact", version.data)) {
          const contactRef = doc(db, "drafts", "contact");
          await setDoc(contactRef, {
            ...version.data,
            lastModified: serverTimestamp(),
          });
          setContactDraft(version.data);
        } else {
          alert(
            "This version is the same as the current published state. No changes to restore.",
          );
        }
      }
    } catch (error) {
      console.error("Error undoing contact publish:", error);
    }
  };

  const undoQualificationPublish = async (versionId) => {
    try {
      const history = await getVersionHistoryBySectionId("qualification");
      const version = history.find((v) => v.id === versionId);
      if (version) {
        if (
          isVersionDifferentFromLastPublished("qualification", version.data)
        ) {
          const qualificationRef = doc(db, "drafts", "qualification");
          await setDoc(qualificationRef, {
            ...version.data,
            lastModified: serverTimestamp(),
          });
          setQualificationDraft(version.data);
        } else {
          alert(
            "This version is the same as the current published state. No changes to restore.",
          );
        }
      }
    } catch (error) {
      console.error("Error undoing qualification publish:", error);
    }
  };

  const undoHomePublish = async (versionId) => {
    try {
      const history = await getVersionHistoryBySectionId("home");
      const version = history.find((v) => v.id === versionId);
      if (version) {
        if (isVersionDifferentFromLastPublished("home", version.data)) {
          const homeRef = doc(db, "drafts", "home");
          await setDoc(homeRef, {
            ...version.data,
            lastModified: serverTimestamp(),
          });
          setHomeDraft(version.data);
        } else {
          alert(
            "This version is the same as the current published state. No changes to restore.",
          );
        }
      }
    } catch (error) {
      console.error("Error undoing home publish:", error);
    }
  };

  const value = {
    user,
    isAdmin,
    adminMode,
    adminPanelOpen,
    themesPanelOpen,
    expandUnsavedChanges,
    loading,
    draftChanges,
    hasUnsavedChanges,
    skillsDraft,
    certificatesDraft,
    projectsDraft,
    contactDraft,
    signInWithGoogle,
    signOutUser,
    toggleAdminMode,
    toggleAdminPanel,
    closeAdminPanel,
    openAdminPanel,
    openAdminPanelWithChanges,
    toggleThemesPanel,
    closeThemesPanel,
    getChangesBySection,
    publishAllDrafts,
    discardDrafts,
    saveSkillsDraft,
    publishSkillsDraft,
    discardSkillsDraft,
    saveCertificatesDraft,
    publishCertificatesDraft,
    discardCertificatesDraft,
    saveProjectsDraft,
    publishProjectsDraft,
    discardProjectsDraft,
    saveContactDraft,
    publishContactDraft,
    discardContactDraft,
    qualificationDraft,
    saveQualificationDraft,
    publishQualificationDraft,
    discardQualificationDraft,
    aboutDraft,
    saveAboutDraft,
    publishAboutDraft,
    discardAboutDraft,
    homeDraft,
    saveHomeDraft,
    publishHomeDraft,
    discardHomeDraft,
    defaultThemesDraft,
    saveDefaultThemesDraft,
    publishDefaultThemesDraft,
    discardDefaultThemesDraft,
    undoDefaultThemesPublish,
    saveVersion,
    getVersionHistoryBySectionId,
    getAllVersions,
    deleteVersion,
    saveFetchedData,
    undoSkillsPublish,
    undoCertificatesPublish,
    undoProjectsPublish,
    undoAboutPublish,
    undoContactPublish,
    undoQualificationPublish,
    undoHomePublish,
  };

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within AdminProvider");
  }
  return context;
};
